import React, { useEffect, useRef, useState } from 'react';
import { DiaryHeader } from '@/components/DiaryHeader';
import { DiaryPreschoolPlans } from '@/components/DiaryPreschoolPlans';
import { DiaryDay } from '@/components/DiaryTable/DiaryDay';
import { DiaryEmptyDay } from '@/components/DiaryTable/DiaryEmptyDay';
import { ProfileStatusBanner } from '@/components/ProfileStatusBanner';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import type { DiaryEntry, PreschoolEducationPlan } from '@/types/diary';
import { isSameApiDate, toApiDate } from '@/utils/date';
import { getPlansInPeriod, isHiddenEmptyWeekend } from '@/utils/diary';

interface ScrollRequest {
  date: string;
  behavior: ScrollBehavior;
}

interface Props {
  entries: DiaryEntry[];
  preschoolPlans: PreschoolEducationPlan[];
  areEntriesLoading: boolean;
  selectedDate: Date;
  minDate: Date;
  maxDate: Date;
  onDateChange: (date: Date) => void;
}

export const DesktopDiary: React.FC<Props> = ({
  entries,
  preschoolPlans,
  areEntriesLoading,
  selectedDate,
  minDate,
  maxDate,
  onDateChange,
}) => {
  const visibleEntries = entries.filter(
    (entry) => !isHiddenEmptyWeekend(entry),
  );
  const weekStart = entries.at(0)?.date;
  const weekEnd = entries.at(-1)?.date;
  const weekPlans =
    weekStart && weekEnd
      ? getPlansInPeriod({
          plans: preschoolPlans,
          from: weekStart,
          to: weekEnd,
        })
      : [];
  const hasWeekContent =
    !areEntriesLoading && (!!visibleEntries.length || !!weekPlans.length);
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollRequest, setScrollRequest] = useState<ScrollRequest | null>(
    () => ({ date: toApiDate(selectedDate), behavior: 'instant' }),
  );

  const selectCalendarDate = (date: Date) => {
    onDateChange(date);
    setScrollRequest({ date: toApiDate(date), behavior: 'smooth' });
  };

  useEffect(() => {
    if (areEntriesLoading || !scrollRequest) {
      return;
    }

    const dayCards =
      listRef.current?.querySelectorAll<HTMLElement>('[data-date]');
    const requestedDayCard = Array.from(dayCards ?? []).find((card) =>
      isSameApiDate(card.dataset.date ?? '', scrollRequest.date),
    );

    requestedDayCard?.scrollIntoView({
      block: 'start',
      behavior: scrollRequest.behavior,
    });
    setScrollRequest(null);
  }, [areEntriesLoading, scrollRequest]);

  return (
    <div className="flex flex-1 flex-col max-md:hidden">
      <ProfileStatusBanner />

      <DiaryHeader
        entries={visibleEntries}
        selectedDate={selectedDate}
        minDate={minDate}
        maxDate={maxDate}
        onDateChange={onDateChange}
        onCalendarSelect={selectCalendarDate}
      />

      <div
        ref={listRef}
        className="flex flex-1 flex-col px-6 [&>*:last-child]:mb-0"
      >
        {areEntriesLoading && (
          <div className="flex flex-1 items-center justify-center">
            <PageSpinner className="size-[20px] text-subtle-foreground" />
          </div>
        )}
        {!areEntriesLoading && !visibleEntries.length && <DiaryEmptyDay />}
        {!areEntriesLoading &&
          visibleEntries.map((entry) => (
            <DiaryDay key={entry.date} entry={entry} />
          ))}

        {!areEntriesLoading && <DiaryPreschoolPlans plans={weekPlans} />}
      </div>

      {hasWeekContent && (
        <DiaryHeader
          isBottom
          entries={visibleEntries}
          selectedDate={selectedDate}
          minDate={minDate}
          maxDate={maxDate}
          onDateChange={onDateChange}
          onCalendarSelect={selectCalendarDate}
        />
      )}
    </div>
  );
};
